"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ChevronRight,
  Stethoscope,
  AlertTriangle,
  GraduationCap,
  GitCompare,
  FolderOpen,
  Circle,
  Bookmark,
  ImageIcon,
  PenTool,
  ClipboardCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SUBSPECIALTIES } from "@/lib/constants";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface DiseaseItem {
  slug: string;
  title: string;
  emergency: boolean;
  examImportance: string | null;
}

interface CategoryItem {
  name: string;
  diseases: DiseaseItem[];
}

type SidebarData = Record<string, CategoryItem[]>;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const pathname = usePathname();
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [sidebarData, setSidebarData] = useState<SidebarData>({});

  // Load sidebar data
  useEffect(() => {
    fetch("/sidebar-data.json", { cache: "no-store" })
      .then((r) => r.json())
      .then(setSidebarData)
      .catch(() => {});
  }, []);

  // Auto-expand current subspecialty and category on navigation
  useEffect(() => {
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length >= 1) {
      const sub = parts[0];
      setExpandedSubs((prev) => {
        if (prev.has(sub)) return prev;
        return new Set(prev).add(sub);
      });

      // Find which category contains the current disease
      if (parts.length >= 2 && sidebarData[sub]) {
        const diseaseSlug = parts[1];
        for (const cat of sidebarData[sub]) {
          if (cat.diseases.some((d) => d.slug === diseaseSlug)) {
            const catKey = `${sub}::${cat.name}`;
            setExpandedCats((prev) => {
              if (prev.has(catKey)) return prev;
              return new Set(prev).add(catKey);
            });
            break;
          }
        }
      }
    }
  }, [pathname, sidebarData]);

  const toggleSub = (id: string) => {
    setExpandedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCat = (key: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isActive = (path: string) => pathname === path;
  const isActivePrefix = (path: string) => pathname.startsWith(path);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-14 z-40 h-[calc(100vh-3.5rem)] w-72 border-r border-sidebar-border bg-sidebar transition-transform duration-200 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <ScrollArea className="h-full">
          <div className="p-3">
            {/* Quick access links */}
            <div className="mb-2 space-y-0.5">
              <Link
                href="/emergency"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/emergency")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <AlertTriangle className="h-4 w-4 text-clinical-red" />
                응급 질환
              </Link>
              <Link
                href="/board-review"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/board-review")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <GraduationCap className="h-4 w-4 text-clinical-amber" />
                Board Review
              </Link>
              <Link
                href="/compare"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/compare")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <GitCompare className="h-4 w-4 text-clinical-purple" />
                감별진단 비교
              </Link>
              <Link
                href="/exams"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/exams")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <ClipboardCheck className="h-4 w-4 text-clinical-green" />
                기출문제
              </Link>
            </div>

            <Separator className="my-2" />

            {/* Subspecialty navigation */}
            <div className="mb-1 px-2.5 py-1.5">
              <Link
                href="/diseases"
                className={`text-sm font-bold tracking-wide ${
                  pathname === "/diseases"
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                } transition-colors`}
              >
                질환
              </Link>
            </div>

            <nav className="space-y-0.5">
              {SUBSPECIALTIES.map((sub) => {
                const Icon = sub.icon;
                const isSubExpanded = expandedSubs.has(sub.id);
                const subActive = isActivePrefix(`/${sub.id}`);
                const categories = sidebarData[sub.id] || [];

                return (
                  <div key={sub.id}>
                    {/* Level 1: Subspecialty */}
                    <button
                      onClick={() => toggleSub(sub.id)}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                        subActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-left">{sub.name}</span>
                      <ChevronRight
                        className={cn(
                          "h-3.5 w-3.5 text-muted-foreground transition-transform",
                          isSubExpanded && "rotate-90"
                        )}
                      />
                    </button>

                    {/* Level 2: Categories */}
                    {isSubExpanded && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-2">
                        {/* 전체 보기 */}
                        <Link
                          href={`/${sub.id}`}
                          className={cn(
                            "block rounded-md px-2.5 py-1.5 text-[13px] transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                            isActive(`/${sub.id}`)
                              ? "bg-sidebar-accent/60 text-sidebar-accent-foreground font-medium"
                              : "text-muted-foreground"
                          )}
                          onClick={onClose}
                        >
                          전체 보기
                        </Link>

                        {categories.map((cat) => {
                          const catKey = `${sub.id}::${cat.name}`;
                          const isCatExpanded = expandedCats.has(catKey);
                          const hasCatActive = cat.diseases.some((d) =>
                            isActive(`/${sub.id}/${d.slug}`)
                          );

                          return (
                            <div key={catKey}>
                              <button
                                onClick={() => toggleCat(catKey)}
                                className={cn(
                                  "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors hover:bg-sidebar-accent",
                                  hasCatActive
                                    ? "text-sidebar-accent-foreground font-medium"
                                    : "text-muted-foreground"
                                )}
                              >
                                <FolderOpen className="h-3.5 w-3.5 shrink-0 opacity-50" />
                                <span className="flex-1 text-left truncate">
                                  {cat.name}
                                </span>
                                <span className="text-[10px] text-muted-foreground/60 mr-1">
                                  {cat.diseases.length}
                                </span>
                                <ChevronRight
                                  className={cn(
                                    "h-3 w-3 text-muted-foreground/50 transition-transform",
                                    isCatExpanded && "rotate-90"
                                  )}
                                />
                              </button>

                              {/* Level 3: Diseases */}
                              {isCatExpanded && (
                                <div className="ml-4 mt-0.5 space-y-0 border-l border-border/50 pl-2">
                                  {cat.diseases.map((disease) => {
                                    const diseasePath = `/${sub.id}/${disease.slug}`;
                                    const isDiseaseActive = isActive(diseasePath);

                                    return (
                                      <Link
                                        key={disease.slug}
                                        href={diseasePath}
                                        className={cn(
                                          "flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                          isDiseaseActive
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-muted-foreground"
                                        )}
                                        onClick={onClose}
                                      >
                                        {disease.emergency ? (
                                          <Circle className="h-1.5 w-1.5 shrink-0 fill-clinical-red text-clinical-red" />
                                        ) : (
                                          <Circle className="h-1.5 w-1.5 shrink-0 fill-border text-border" />
                                        )}
                                        <span className="truncate leading-snug">
                                          {disease.title.split(" (")[0]}
                                        </span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <Separator className="my-2" />

            <nav className="space-y-0.5">
              <Link
                href="/papers"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/papers")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <Stethoscope className="h-4 w-4" />
                논문 라이브러리
              </Link>
              <Link
                href="/images"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/images")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <ImageIcon className="h-4 w-4" />
                이미지 탐색기
              </Link>
              <Link
                href="/writing"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/writing")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <PenTool className="h-4 w-4" />
                논문 작성
              </Link>
              <Link
                href="/bookmarks"
                className={cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors hover:bg-sidebar-accent",
                  isActivePrefix("/bookmarks")
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground"
                )}
                onClick={onClose}
              >
                <Bookmark className="h-4 w-4" />
                북마크
              </Link>
            </nav>
          </div>
        </ScrollArea>
      </aside>
    </>
  );
}
